import { useToastManager } from "@porsche-design-system/components-react"
import type { CapCustomer } from "@slfinrtl/ui-bff-routes"
import { offerTransferVehicleRoute } from "@slfinrtl/ui-bff-routes"
import { useCallback, useContext, useState } from "react"
import { useIntl } from "react-intl"
import { trackEvent } from "src/components/adobeAnalytics"
import { ModalContext } from "src/context/modal/ModalContext"
import { useBffDynamic } from "src/hooks"
import { TRACK_TRANSFER_VEHICLE } from "src/screens/offers/common/tracking"
import { offerMessages } from "./offer.messages"

export interface UseTransferVehicleModalProps {
	modalId: string
	vehicleId: string
}

interface UseTransferVehicleModalReturn {
	customer: CapCustomer | undefined
	isTransferring: boolean
	onCancel: () => void
	onConfirmTransfer: () => void
	onCustomerSelect: (customer: { businessPartnerId: string; leadId?: string; name: string }) => void
}

export const useTransferVehicleModal = ({
	modalId,
	vehicleId,
}: UseTransferVehicleModalProps): UseTransferVehicleModalReturn => {
	const [isTransferring, setIsTransferring] = useState(false)
	const [customer, setCustomer] = useState<CapCustomer | undefined>(undefined)
	const { hideModal } = useContext(ModalContext)
	const { addMessage } = useToastManager()
	const { formatMessage } = useIntl()

	const transferVehicle = useBffDynamic(offerTransferVehicleRoute, "inline")

	const onCancel = useCallback(() => {
		hideModal(modalId)
	}, [hideModal, modalId])

	const onCustomerSelect = useCallback(
		(selectedCustomer: { businessPartnerId: string; leadId?: string; name: string }) => {
			setCustomer({
				businessPartnerId: selectedCustomer.businessPartnerId,
				leadId: selectedCustomer.leadId,
				name: selectedCustomer.name,
				type: "CAP",
			})
		},
		[],
	)

	const onConfirmTransfer = useCallback(() => {
		if (!customer) {
			return
		}

		trackEvent(TRACK_TRANSFER_VEHICLE)
		setIsTransferring(true)
		transferVehicle({
			body: {
				customer,
				vehicle: {
					identifier: {
						type: "INVENTORY_VEHICLE_ID",
						vehicleId,
					},
					type: "STOCK_CAR",
				},
			},
			pathParams: {},
			queryParams: {},
		})
			.then(() => {
				addMessage({
					state: "success",
					text: formatMessage(offerMessages.transferVehicleSuccessToast),
				})
				hideModal(modalId)
			})
			.finally(() => setIsTransferring(false))
	}, [transferVehicle, customer, vehicleId, addMessage, formatMessage, hideModal, modalId])

	return {
		customer,
		isTransferring,
		onCancel,
		onConfirmTransfer,
		onCustomerSelect,
	}
}
